import {createStyles, keyframes} from '@mantine/core';
import React, {useState} from 'react';

import {
  createCheckStatusInstrumentQuery,
  pollInstrumentQueryStatus,
} from 'instruments/instrument-data/InstrumentDataDAO';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface RefreshConnectionStatusButtonProps {
  connectionId: string;
  onRefresh?: () => void;
  /** When set, the button is disabled and its tooltip explains why. */
  disabledReason?: string;
}

const TOOLTIP_TEXT = 'Refresh connection status';
const REFRESHING_TOOLTIP_TEXT = 'Checking connection status…';

const spin = keyframes({
  from: {transform: 'rotate(0deg)'},
  to: {transform: 'rotate(360deg)'},
});

const useStyles = createStyles(() => ({
  spinning: {
    animation: `${spin} 1s linear infinite`,
  },
}));

export default function RefreshConnectionStatusButton(
  props: RefreshConnectionStatusButtonProps
): JSX.Element {
  const {classes} = useStyles();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      const {instrumentQueryId} = await createCheckStatusInstrumentQuery(props.connectionId);
      // Submitting the query returns immediately, but the CHECK_STATUS round-trip to the adapter
      // takes 1-2 min. Poll until it completes so the spinner reflects real progress instead of
      // flashing and leaving the status looking unchanged.
      await pollInstrumentQueryStatus(instrumentQueryId);
      // Submitting or polling can fail or time out (the check may still complete adapter-side).
      // Either way we intentionally swallow and refresh below to surface the latest known status
      // rather than an error.
      // eslint-disable-next-line benchlint/always-throw-from-catch -- intentional swallow, see above
    } catch {
      // Intentionally empty.
    } finally {
      props.onRefresh?.();
      setIsRefreshing(false);
    }
  };

  const isDisabled = isRefreshing || props.disabledReason != null;
  const tooltipText = isRefreshing ? REFRESHING_TOOLTIP_TEXT : (props.disabledReason ?? TOOLTIP_TEXT);

  return (
    <Tooltip text={tooltipText} disableContentSelection={true}>
      <Button
        aria-disabled={isDisabled}
        aria-label={TOOLTIP_TEXT}
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="metadata-header-button-spacing-left"
        disabled={isDisabled}
        icon={<BenchlingIcon icon="rotate-right" className={isRefreshing ? classes.spinning : undefined} />}
        onClick={handleRefresh}
        size={ButtonSize.SMALL}
      />
    </Tooltip>
  );
}
